import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import Notification from '../common/Notification';
import './Gallery.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://krmmadhubanibackend.onrender.com';

const Gallery = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('announcements'); // 'announcements' | 'whatsapp'
  const [mediaFilter, setMediaFilter] = useState('all'); // 'all' | 'pictures' | 'video' | 'audio' | 'documents'
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selection
  const [selectMode, setSelectMode] = useState(false);
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // Preview
  const [previewImage, setPreviewImage] = useState(null);

  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 3000);
  };

  // ===== FETCH ALL IMAGES =====
  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);
      let allImages = [];

      if (tab === 'announcements') {
        const { data, error } = await supabase
          .from('announcements')
          .select('id, title, notice_date, image_urls, created_at')
          .not('image_urls', 'is', null)
          .order('created_at', { ascending: false });

        if (error) throw error;

        (data || []).forEach(ann => {
          if (ann.image_urls && Array.isArray(ann.image_urls)) {
            ann.image_urls.forEach(url => {
              if (url) {
                allImages.push({
                  url,
                  announcementId: ann.id,
                  title: ann.title,
                  noticeDate: ann.notice_date,
                  createdAt: ann.created_at,
                  source: 'announcements',
                });
              }
            });
          }
        });
      } else {
        // WhatsApp media (images, audio, video)
        let query = supabase
          .from('whatsapp_messages')
          .select(`
            id,
            conversation_id,
            media_url,
            message_type,
            media_mimetype,
            media_filename,
            sent_at,
            direction,
            sender_phone,
            whatsapp_conversations(contact_name, phone_number)
          `)
          .not('media_url', 'is', null)
          .order('sent_at', { ascending: false });

        // Filter by media type
        if (mediaFilter === 'pictures') {
          query = query.eq('message_type', 'image');
        } else if (mediaFilter === 'video') {
          query = query.eq('message_type', 'video');
        } else if (mediaFilter === 'audio') {
          query = query.in('message_type', ['audio', 'voice']);
        } else if (mediaFilter === 'documents') {
          query = query.eq('message_type', 'document');
        } else {
          // 'all' - fetch image, video, audio, voice, document
          query = query.in('message_type', ['image', 'video', 'audio', 'voice', 'document']);
        }

        const { data, error } = await query;

        if (error) throw error;

        allImages = (data || []).map(msg => {
          const conv = msg.whatsapp_conversations || {};
          const contactName = conv.contact_name || conv.phone_number || msg.sender_phone || 'Unknown';
          const sender = msg.direction === 'outgoing' ? 'You' : contactName;
          return {
            url: msg.media_url,
            messageId: msg.id,
            conversationId: msg.conversation_id,
            createdAt: msg.sent_at,
            sender,
            source: 'whatsapp',
            mediaType: msg.message_type,
            mediaMimetype: msg.media_mimetype,
            mediaFilename: msg.media_filename,
          };
        });
      }

      // Deduplicate by URL (same image attached to many announcements/messages)
      const seen = new Set();
      const unique = allImages.filter(img => {
        if (seen.has(img.url)) return false;
        seen.add(img.url);
        return true;
      });

      // Ensure newest first
      unique.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setImages(unique);
    } catch (err) {
      console.error('Error fetching gallery:', err);
      showNotification('Failed to load gallery', 'error');
    } finally {
      setLoading(false);
    }
  }, [tab, mediaFilter]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  // ===== SELECTION =====
  const toggleSelect = (url) => {
    setSelectedUrls(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]
    );
  };

  const selectAll = () => { setSelectedUrls(images.map(i => i.url)); };
  const clearSelection = () => { setSelectedUrls([]); };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedUrls([]);
  };

  // ===== DELETE =====
  const handleDelete = async () => {
    if (selectedUrls.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedUrls.length} image(s)? This cannot be undone.`)) return;

    setDeleting(true);
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      if (!token) {
        showNotification('Session expired. Please login again.', 'error');
        setDeleting(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/gallery/delete-images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ imageUrls: selectedUrls, source: tab }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showNotification(`Deleted ${result.deleted} image(s) successfully.`, 'success');
        setImages(prev => prev.filter(img => !selectedUrls.includes(img.url)));
        exitSelectMode();
      } else {
        showNotification(result.error || 'Failed to delete images', 'error');
      }
    } catch (err) {
      console.error('Delete error:', err);
      showNotification('Failed to delete images', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const getImageMeta = (img) => {
    if (!img) return '';
    if (img.source === 'announcements') {
      const date = img.noticeDate || img.createdAt;
      return `Sent: ${formatDateTime(date)}${img.title ? ` • ${img.title}` : ''}`;
    }
    return `${img.sender} • ${formatDateTime(img.createdAt)}`;
  };

  return (
    <div className="gallery-container">
      {notification.show && <Notification message={notification.message} type={notification.type} />}

      {/* Header */}
      <div className="gallery-header">
        {selectMode ? (
          <div className="gallery-header-select">
            <button className="btn btn-secondary" onClick={exitSelectMode}>
              <i className="fas fa-times"></i> Cancel
            </button>
            <span className="selected-count">{selectedUrls.length} selected</span>
            <button className="btn btn-secondary" onClick={selectAll}>Select All</button>
            <button
              className="btn btn-primary"
              onClick={() => {
                const selected = images.filter(img => selectedUrls.includes(img.url));
                exitSelectMode();
                navigate('/announcement/bulk-notice', { state: { galleryImages: selected } });
              }}
              disabled={selectedUrls.length === 0}
            >
              <i className="fas fa-paper-plane"></i> Send
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={selectedUrls.length === 0 || deleting}
            >
              {deleting ? (
                <><i className="fas fa-spinner fa-spin"></i> Deleting...</>
              ) : (
                <><i className="fas fa-trash"></i> Delete</>
              )}
            </button>
          </div>
        ) : (
          <div className="gallery-header-normal">
            <h2>Gallery</h2>
            <div className="gallery-tabs">
              <button
                className={`gallery-tab ${tab === 'announcements' ? 'active' : ''}`}
                onClick={() => { setTab('announcements'); setSelectedUrls([]); setSelectMode(false); }}
              >
                <i className="fas fa-bullhorn"></i> Announcements
              </button>
              <button
                className={`gallery-tab ${tab === 'whatsapp' ? 'active' : ''}`}
                onClick={() => { setTab('whatsapp'); setSelectedUrls([]); setSelectMode(false); }}
              >
                <i className="fab fa-whatsapp"></i> WhatsApp
              </button>
            </div>
            {tab === 'whatsapp' && (
              <div className="gallery-media-filters">
                {['all', 'pictures', 'video', 'audio', 'documents'].map(f => (
                  <button
                    key={f}
                    className={`gallery-media-filter ${mediaFilter === f ? 'active' : ''}`}
                    onClick={() => { setMediaFilter(f); setSelectedUrls([]); setSelectMode(false); }}
                  >
                    {f === 'all' && <i className="fas fa-th"></i>}
                    {f === 'pictures' && <i className="fas fa-image"></i>}
                    {f === 'video' && <i className="fas fa-video"></i>}
                    {f === 'audio' && <i className="fas fa-headphones"></i>}
                    {f === 'documents' && <i className="fas fa-file-pdf"></i>}
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            )}
            <button className="btn btn-primary" onClick={() => setSelectMode(true)}>
              <i className="fas fa-check-square"></i> Select
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="gallery-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading gallery...</p>
        </div>
      ) : images.length === 0 ? (
        <div className="gallery-empty">
          <i className={`fas ${mediaFilter === 'documents' ? 'fa-file-pdf' : mediaFilter === 'audio' ? 'fa-headphones' : mediaFilter === 'video' ? 'fa-video' : 'fa-images'}`}></i>
          <h3>No {mediaFilter === 'all' ? 'Media' : mediaFilter.charAt(0).toUpperCase() + mediaFilter.slice(1)}</h3>
          <p>{tab === 'announcements' ? 'Announcement photos' : `WhatsApp ${mediaFilter === 'all' ? 'media' : mediaFilter}`} will appear here.</p>
        </div>
      ) : (
        <div className="gallery-grid">
          {images.map((img, index) => (
            <div
              key={`${img.url}_${index}`}
              className={`gallery-item ${selectedUrls.includes(img.url) ? 'selected' : ''} ${img.mediaType === 'audio' || img.mediaType === 'voice' ? 'gallery-item-audio' : ''} ${img.mediaType === 'document' ? 'gallery-item-document' : ''}`}
              onClick={() => selectMode ? toggleSelect(img.url) : (img.mediaType !== 'audio' && img.mediaType !== 'voice') && (img.mediaType === 'document' ? window.open(img.url, '_blank') : setPreviewImage(img))}
            >
              {img.mediaType === 'document' ? (
                <div className="gallery-document-item">
                  <i className="fas fa-file-pdf"></i>
                  <span className="gallery-doc-name">{img.mediaFilename || 'Document'}</span>
                  <span className="gallery-doc-sender">{img.sender} • {formatDate(img.createdAt)}</span>
                </div>
              ) : img.mediaType === 'video' ? (
                <>
                  <video src={img.url} preload="metadata" />
                  <div className="gallery-video-badge"><i className="fas fa-play"></i></div>
                </>
              ) : (img.mediaType === 'audio' || img.mediaType === 'voice') ? (
                <div className="gallery-audio-item">
                  <i className="fas fa-headphones"></i>
                  <audio controls preload="metadata" onClick={(e) => e.stopPropagation()}>
                    <source src={img.url} />
                  </audio>
                  <span className="gallery-audio-sender">{img.sender} • {formatDate(img.createdAt)}</span>
                </div>
              ) : (
                <img src={img.url} alt="" loading="lazy" />
              )}
              {selectMode && (
                <div className="gallery-checkbox">
                  <i className={`fas ${selectedUrls.includes(img.url) ? 'fa-check-square' : 'fa-square'}`}></i>
                </div>
              )}
              {img.title && (
                <div className="gallery-caption">
                  <p>{img.title}</p>
                  <span>{formatDate(img.createdAt)}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewImage && (
        <div className="gallery-preview-overlay" onClick={() => setPreviewImage(null)}>
          <button className="preview-close" onClick={(e) => { e.stopPropagation(); setPreviewImage(null); }}>
            <i className="fas fa-times"></i>
          </button>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            {previewImage.mediaType === 'video' ? (
              <video controls autoPlay preload="metadata" style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 8 }}>
                <source src={previewImage.url} />
              </video>
            ) : (
              <img src={previewImage.url} alt="Preview" />
            )}
            <div className="preview-meta">
              <p>{getImageMeta(previewImage)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Deleting overlay */}
      {deleting && (
        <div className="gallery-deleting-overlay">
          <div className="gallery-deleting-box">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Deleting images...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
